import { Box, Chip, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { getDeepViewValuesEntry } from "../../deepViewFieldMap";
import { isNegativeValue } from "../../deepViewNumbers";

interface Props {
  /** API key of the column the user clicked, e.g. "total_income_usd" */
  columnKey?: string;
  /** Label of the column the user clicked, e.g. "Total Income (USD)" */
  columnLabel?: string;
}

export default function AdjustmentDetails({ columnKey, columnLabel }: Props) {
  const deepViewData = useSelector(
    (state: any) => state?.deepView?.deepViewData,
  );
  const adjustments =
    getDeepViewValuesEntry(deepViewData, columnKey)?.adjustments ?? [];

  if (adjustments.length === 0) return null;

  return (
    <>
      <Box mt={1}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            color: "#00000099",
            fontSize: "14px",
            textTransform: "uppercase",
            mb: 0.5,
          }}
        >
          Adjustment Details
        </Typography>

        {adjustments.map((adjustment: any, index: number) => (
          <Box key={adjustment?.adj_id ?? index} mb={1.5}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Chip
                label={`#${adjustment?.adj_id}`}
                variant="outlined"
                sx={{
                  borderColor: "#9C27B080",
                  background: "#9C27B00A",
                  color: "#7B1FA2",
                  fontWeight: 400,
                  fontSize: 12,
                  height: 20,
                  borderRadius: 1,
                  p: 0,
                  "& .MuiChip-label": {
                    px: 0.4,
                  },
                }}
              />

              <Typography fontSize={12}>
                {adjustment?.date} · Type:{" "}
                <Box component="span" fontWeight={700}>
                  {adjustment?.type}
                </Box>
              </Typography>

              <Typography
                fontSize={8}
                color="text.secondary"
                fontStyle="italic"
              >
                By {adjustment?.created_by} on{" "}
                {adjustment?.created
                  ? dayjs(adjustment.created).format("YYYY-MM-DD HH:mm")
                  : "-"}
              </Typography>
            </Stack>

            {adjustment?.comment && (
              <Typography
                sx={{
                  mt: 0.5,
                  ml: 6,
                  fontSize: 14,
                  fontStyle: "italic",
                  color: "#000000DE",
                }}
              >
                &quot;{adjustment.comment}&quot;
              </Typography>
            )}

            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 1, ml: 6 }}
            >
              {(() => {
                const isNegative = isNegativeValue(adjustment?.value);
                return (
                  <Chip
                    label={`${columnLabel}: ${adjustment?.value ?? "-"}`}
                    variant="outlined"
                    color={isNegative ? "error" : "default"}
                    sx={{
                      borderRadius: 1,
                      fontSize: 12,
                      height: 20,
                      bgcolor: isNegative ? "#FFF5F5" : "#FAFAFA",
                      "& .MuiChip-label": {
                        px: 0.5,
                      },
                    }}
                  />
                );
              })()}
            </Stack>
          </Box>
        ))}
      </Box>
    </>
  );
}
